import React, { useMemo, useRef, useState } from 'react';
import ThemedDatePicker from '../ThemedDatePicker';
import Dropdown from '../../../Components/CustomDropdown/customdropdown';
import CustomDropdownWithFetch from '../../../Components/CustomDropdown/custom-dropdown-fetchAPI';
import StatusButton from '../../../Components/onboarding/status-button';
import {
  getAdditionalDocumentFields,
  getAdditionalDocumentTypeKey,
  requiresBackFile,
} from './additionalDocumentSchema';

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) return parsed;
  const fallback = new Date(`${value}T00:00:00`);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

const formatDate = (date, monthOnly = false) => {
  if (!date || Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  if (monthOnly) return `${year}-${month}`;
  return [year, month, String(date.getDate()).padStart(2, '0')].join('-');
};

const getFileData = (file) => {
  if (!file || typeof file !== 'object') return file ?? null;
  if (
    file.value &&
    typeof file.value === 'object' &&
    (file.value.file_id || file.value.file_name)
  )
    return file.value;
  if (file.file_id || file.file_name) return file;
  return null;
};

const FileIcon = ({ className = 'h-5 w-5' }) => (
  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" className={className}>
    <path
      d="M7.5 3.75h6.25L18.5 8.5v11.75H7.5a2 2 0 0 1-2-2v-12.5a2 2 0 0 1 2-2Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path
      d="M13.5 3.9v4.85h4.65M8.5 13h5.5M8.5 16.25h7"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const UploadIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" className="h-6 w-6">
    <path
      d="M12 15.75V4.5m0 0L7.75 8.75M12 4.5l4.25 4.25M5.25 14.75v3A1.75 1.75 0 0 0 7 19.5h10a1.75 1.75 0 0 0 1.75-1.75v-3"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PlusIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 20 20" fill="none" className="h-4 w-4">
    <path
      d="M10 4v12M4 10h12"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const ChevronIcon = ({ expanded }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 20 20"
    fill="none"
    className={`h-4 w-4 transition-transform duration-200 ${
      expanded ? 'rotate-180' : ''
    }`}
  >
    <path
      d="m5 7.5 5 5 5-5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TrashIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" className="h-5 w-5">
    <path
      d="M5 7h14M10 11v6M14 11v6M8 7l.7-2h6.6l.7 2m-9 0 .7 13h8.6l.7-13"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AdditionalDocumentSection = ({
  options = [],
  documents = [],
  onAddDocument,
  onRemoveDocument,
  onDocumentTypeChange,
  onFieldChange,
  onFileSelected,
  onRemoveFile,
  onPreviewFile,
  onStatusClick,
  countryOptions = [],
  stateOptions = [],
  fetchCountries,
  disabled = false,
}) => {
  const inputRefs = useRef({});
  const [draggingId, setDraggingId] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);

  const handleAddDocument = () => {
    onAddDocument?.();
    setIsExpanded(true);
  };

  const usedDocumentTypeKeys = useMemo(
    () =>
      documents
        .map((document) => getAdditionalDocumentTypeKey(document.documentType))
        .filter(Boolean),
    [documents],
  );

  const availableOptionCount = options.filter(
    (option) =>
      !usedDocumentTypeKeys.includes(getAdditionalDocumentTypeKey(option)),
  ).length;

  const getOptionsForDocument = (document) => {
    const currentKey = getAdditionalDocumentTypeKey(document.documentType);
    return options.filter((option) => {
      const optionKey = getAdditionalDocumentTypeKey(option);
      return (
        optionKey === currentKey ||
        !usedDocumentTypeKeys.some(
          (usedKey) => usedKey === optionKey && usedKey !== currentKey,
        )
      );
    });
  };

  const renderField = (document, fieldDefinition) => {
    const value = document.values?.[fieldDefinition.name] ?? '';
    /* Per-field review state hydrated from the preview API — drives the
       StatusButton (status + comments count), same as the primary
       document fields. Falls back to a pending state for new rows. */
    const fieldState = document.fieldStates?.[fieldDefinition.name] || {
      status: 'pending',
      comments: [],
    };
    const selectedValue = value?.id ?? value?.value ?? value;
    const selectedLabel =
      value?.name ?? value?.label ?? String(selectedValue ?? '');
    const error = document.errors?.[fieldDefinition.name];
    const commonClassName = `w-full rounded-xl border bg-white px-3.5 py-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-[#375AE7] focus:ring-4 focus:ring-[#375AE7]-100 ${
      error
        ? 'border-rose-400 focus:border-rose-500 focus:ring-rose-100'
        : 'border-slate-200'
    }`;
    const fieldName = fieldDefinition.name;
    const isCountryField =
      fieldName.includes('country_of_issue') ||
      fieldName.includes('country_of_birth');
    const isStateField =
      fieldName.includes('state_of_issue') ||
      fieldName.includes('registration_state') ||
      fieldName === 'state';
    const locationOptions = isCountryField ? countryOptions : stateOptions;

    return (
      <div key={fieldDefinition.name} className="min-w-0">
        <label
          htmlFor={`${document.id}-${fieldDefinition.name}`}
          className="mb-1.5 flex items-center gap-1 text-xs font-semibold tracking-wide text-slate-600"
        >
          <span>{fieldDefinition.label}</span>
          {fieldDefinition.required && <span className="text-rose-500">*</span>}
          {fieldDefinition.requiredGroup && (
            <span className="ml-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">
              one required
            </span>
          )}
          <span className="ml-auto">
            <StatusButton
              status={fieldState.status}
              count={(fieldState.comments || []).length}
              openSection={() =>
                onStatusClick?.(document, fieldDefinition.name, value)
              }
            />
          </span>
        </label>

        {isCountryField ? (
          <CustomDropdownWithFetch
            label=""
            options={countryOptions}
            search
            preselectedValue={
              countryOptions.find(
                (option) => String(option.value) === String(selectedValue),
              ) ||
              (selectedValue
                ? { value: selectedValue, label: selectedLabel }
                : {})
            }
            onSelect={(option) =>
              onFieldChange?.(
                document.id,
                fieldDefinition.name,
                option?.value ?? '',
              )
            }
            colorLabel=""
            fetchOptions={fetchCountries}
          />
        ) : isStateField ? (
          <Dropdown
            label=""
            options={stateOptions}
            preselectedValue={
              locationOptions.find(
                (option) => String(option.value) === String(selectedValue),
              ) ||
              (selectedValue
                ? { value: selectedValue, label: selectedLabel }
                : {})
            }
            onSelect={(option) =>
              onFieldChange?.(
                document.id,
                fieldDefinition.name,
                option?.value ?? '',
              )
            }
            disabled={disabled}
            parentClass={error ? 'border-rose-400 border-2' : ''}
          />
        ) : fieldDefinition.type === 'select' ? (
          <Dropdown
            label=""
            options={fieldDefinition.options || []}
            preselectedValue={
              fieldDefinition.options?.find(
                (option) => String(option.value) === String(selectedValue),
              ) ||
              (selectedValue
                ? { value: selectedValue, label: selectedLabel }
                : {})
            }
            onSelect={(option) =>
              onFieldChange?.(
                document.id,
                fieldDefinition.name,
                option?.value ?? '',
              )
            }
            disabled={disabled}
            parentClass={error ? 'border-rose-400 border-2' : ''}
          />
        ) : fieldDefinition.type === 'date' ||
          fieldDefinition.type === 'month' ? (
          <ThemedDatePicker
            id={`${document.id}-${fieldDefinition.name}`}
            selected={toDate(value)}
            onChange={(date) =>
              onFieldChange?.(
                document.id,
                fieldDefinition.name,
                formatDate(date, fieldDefinition.type === 'month'),
              )
            }
            disabled={disabled}
            minDate={new Date()}
            dateFormat={
              fieldDefinition.type === 'month' ? 'MM/yyyy' : 'dd/MM/yyyy'
            }
            showMonthYearPicker={fieldDefinition.type === 'month'}
            showMonthDropdown={fieldDefinition.type !== 'month'}
            showYearDropdown={fieldDefinition.type !== 'month'}
            dropdownMode="scroll"
            scrollableYearDropdown={fieldDefinition.type !== 'month'}
            yearDropdownItemNumber={100}
            placeholderText={
              fieldDefinition.type === 'month'
                ? 'Select month and year'
                : 'Select date'
            }
            invalid={Boolean(error)}
            wrapperClassName="w-full"
            className={commonClassName}
          />
        ) : (
          <input
            id={`${document.id}-${fieldDefinition.name}`}
            type={
              fieldDefinition.type === 'number'
                ? 'number'
                : fieldDefinition.type
            }
            value={value}
            min={fieldDefinition.min}
            max={fieldDefinition.max}
            maxLength={fieldDefinition.maxLength}
            disabled={disabled}
            placeholder={
              fieldDefinition.placeholder ||
              `Enter ${fieldDefinition.label.toLowerCase()}`
            }
            onChange={(event) =>
              onFieldChange?.(
                document.id,
                fieldDefinition.name,
                event.target.value,
              )
            }
            className={commonClassName}
          />
        )}
        {error && <p className="mt-1.5 text-xs text-rose-600">{error}</p>}
      </div>
    );
  };

  const renderUpload = (document, side = 'front') => {
    const isBack = side === 'back';
    const fileState = isBack ? document.backFile : document.file;
    const file = getFileData(fileState);
    const fileLocked = disabled || fileState?.status === 'approved';
    const inputKey = `${document.id}-${side}`;
    const inputId = `additional-document-file-${inputKey}`;
    const uploadLabel = requiresBackFile(document.documentType)
      ? isBack
        ? 'Driving licence back'
        : 'Driving licence front'
      : 'Document file';

    const handleDrop = (event) => {
      event.preventDefault();
      setDraggingId(null);
      if (!fileLocked) {
        onFileSelected?.(document.id, side, event.dataTransfer.files?.[0]);
      }
    };

    return (
      <div>
        <div className="mb-2 flex items-end justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <p className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">
                {uploadLabel} <span className="text-rose-500">*</span>
              </p>
              <StatusButton
                status={fileState?.status ?? 'pending'}
                count={(fileState?.comments || []).length}
                openSection={() =>
                  onStatusClick?.(document, isBack ? 'backFile' : 'file', file)
                }
              />
            </div>
            <p className="mt-1 text-xs text-slate-400">
              PDF, PNG or JPEG · maximum 10 MB
            </p>
          </div>
          <div className="hidden gap-1.5 sm:flex">
            {['PDF', 'PNG', 'JPEG'].map((format) => (
              <span
                key={format}
                className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-bold text-slate-500"
              >
                {format}
              </span>
            ))}
          </div>
        </div>

        {!file ? (
          <label
            htmlFor={inputId}
            onDragEnter={(event) => {
              event.preventDefault();
              if (!fileLocked) setDraggingId(inputKey);
            }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={() => setDraggingId(null)}
            onDrop={handleDrop}
            className={`flex min-h-[145px] cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-7 text-center transition ${
              fileLocked
                ? 'cursor-not-allowed border-slate-200 bg-slate-100/70 opacity-60'
                : draggingId === inputKey
                  ? 'border-[#375AE7] bg-[#375AE7]/10'
                  : 'border-slate-300 bg-white/70 hover:border-[#375AE7] hover:bg-[#375AE7]/10'
            }`}
          >
            <input
              id={inputId}
              ref={(node) => {
                inputRefs.current[inputKey] = node;
              }}
              type="file"
              accept=".pdf,.png,.jpeg,.jpg,application/pdf,image/png,image/jpeg"
              disabled={fileLocked}
              className="hidden"
              onChange={(event) => {
                onFileSelected?.(document.id, side, event.target.files?.[0]);
                event.target.value = '';
              }}
            />
            <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#375AE7]/10 text-[#375AE7]">
              <UploadIcon />
            </span>
            <span className="text-sm font-semibold text-slate-700">
              Drop the {uploadLabel.toLowerCase()} here or click to browse
            </span>
            <span className="mt-1 text-xs text-slate-400">
              Use a clear colour copy for faster verification
            </span>
          </label>
        ) : (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-2xl border border-emerald-200 bg-emerald-50/70 px-4 py-3">
            <button
              type="button"
              onClick={() => onPreviewFile?.(file)}
              className="flex min-w-0 items-center gap-3 text-left"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-emerald-600 shadow-sm">
                <FileIcon />
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-slate-800 underline decoration-emerald-400 underline-offset-2">
                  {file.file_name || `${uploadLabel} uploaded`}
                </span>
                <span className="mt-0.5 block text-xs font-medium text-emerald-700">
                  Upload completed
                </span>
              </span>
            </button>
            {!fileLocked && (
              <button
                type="button"
                onClick={() => {
                  onRemoveFile?.(document.id, side);
                  if (inputRefs.current[inputKey]) {
                    inputRefs.current[inputKey].value = null;
                  }
                }}
                className="rounded-xl p-2 text-rose-500 transition hover:bg-rose-100 hover:text-rose-700"
                aria-label={`Remove ${uploadLabel}`}
              >
                <TrashIcon />
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <section className="relative mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-indigo-50 shadow-[0_14px_45px_rgba(43,56,120,0.08)]">
      <div className="relative border-b border-slate-200/80 px-5 py-5 sm:px-7">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-wrap gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#375AE7] text-white shadow-lg ">
              <FileIcon />
            </div>
            <div>
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <h2 className="text-lg font-bold tracking-tight text-slate-900">
                  Additional documents
                </h2>
                <span className="rounded-full bg-[#375AE7]/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-[#375AE7]">
                  Optional
                </span>
              </div>
              <p className="max-w-2xl text-sm leading-6 text-slate-500">
                Add supporting documents using a different document type for
                each.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 self-start">
            {documents.length > 0 && (
              <span className="rounded-full bg-white px-3 py-2 text-xs font-semibold text-slate-600 shadow-sm ring-1 ring-slate-200">
                {documents.length}{' '}
                {documents.length === 1 ? 'document' : 'documents'}
              </span>
            )}
            <button
              type="button"
              onClick={() => setIsExpanded((expanded) => !expanded)}
              aria-expanded={isExpanded}
              aria-controls="additional-documents-content"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-2.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-[#375AE7] hover:bg-[#375AE7]/10 hover:text-[#375AE7]"
            >
              <span>{isExpanded ? 'Collapse' : 'Expand'}</span>
              <ChevronIcon expanded={isExpanded} />
            </button>
            <button
              type="button"
              onClick={handleAddDocument}
              disabled={disabled || availableOptionCount === 0}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#375AE7] px-4 py-2.5 text-sm font-semibold text-white shadow-lg transition hover:bg-[#375AE7]/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <PlusIcon />
              Add document
            </button>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div
          id="additional-documents-content"
          className="relative space-y-5 px-5 py-6 sm:px-7"
        >
          {documents.length === 0 ? (
            <div className="flex min-h-[160px] flex-col items-center justify-center rounded-2xl border border-dashed border-[#375AE7] bg-white/60 px-6 py-8 text-center">
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#375AE7]/10 text-[#375AE7]">
                <FileIcon className="h-6 w-6" />
              </div>
              <p className="text-sm font-semibold text-slate-700">
                No additional documents added
              </p>
              <p className="mt-1 max-w-md text-xs leading-5 text-slate-500">
                Select Add document to upload multiple documents such as a birth
                certificate, visa or Medicare card.
              </p>
            </div>
          ) : (
            documents.map((document, index) => {
              const selectedTypeKey = getAdditionalDocumentTypeKey(
                document.documentType,
              );
              const fields = getAdditionalDocumentFields(document.documentType);
              const documentOptions = getOptionsForDocument(document);

              return (
                <article
                  key={document.id}
                  className="rounded-2xl border border-slate-200 bg-white/80 p-4 shadow-sm sm:p-5"
                >
                  <div className="mb-5 flex flex-col gap-3 border-b border-slate-100 pb-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex items-start gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#375AE7]/10 text-sm font-bold text-[#375AE7]">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <div>
                        <p className="text-sm font-bold text-slate-900">
                          Additional document {index + 1}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">
                          Choose a unique type and complete only its relevant
                          fields.
                        </p>
                      </div>
                    </div>
                    {!disabled && (
                      <button
                        type="button"
                        onClick={() => onRemoveDocument?.(document.id)}
                        className="inline-flex items-center gap-1.5 self-start rounded-full px-3 py-1.5 text-xs font-semibold text-rose-600 transition hover:bg-rose-50"
                      >
                        <TrashIcon />
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="max-w-xs">
                    <label className="mb-2 block text-xs font-bold uppercase tracking-[0.12em] text-slate-500">
                      Document type
                    </label>
                    <Dropdown
                      label=""
                      options={documentOptions}
                      preselectedValue={document.documentType || {}}
                      onSelect={(option) =>
                        onDocumentTypeChange?.(document.id, option)
                      }
                      disabled={disabled}
                    />
                    {documentOptions.length === 0 && (
                      <p className="mt-2 text-xs text-amber-700">
                        All available document types have already been selected.
                      </p>
                    )}
                  </div>

                  {!document.documentType?.value &&
                  !document.documentType?.code ? (
                    <div className="mt-5 rounded-2xl border border-dashed border-slate-200 bg-slate-50/70 px-5 py-6 text-center text-xs text-slate-500">
                      Select a document type to reveal its relevant fields.
                    </div>
                  ) : (
                    <div className="mt-6 space-y-6">
                      <div className="flex flex-col gap-2 rounded-2xl border border-[#375AE7] bg-[#375AE7]/10 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm font-bold text-[#375AE7]">
                            {document.documentType.label}
                          </p>
                          <p className="text-xs text-[#375AE7]/70">
                            Complete the document details and upload the
                            required file.
                          </p>
                        </div>
                        <span className="inline-flex w-fit items-center rounded-full bg-white px-3 py-1 text-xs font-semibold text-[#375AE7] shadow-sm ring-1 ring-[#375AE7]">
                          {fields.length} relevant{' '}
                          {fields.length === 1 ? 'field' : 'fields'}
                        </span>
                      </div>

                      {fields.length > 0 && (
                        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
                          {fields.map((fieldDefinition) =>
                            renderField(document, fieldDefinition),
                          )}
                        </div>
                      )}

                      {selectedTypeKey === 'death_certificate' && (
                        <p className="-mt-2 text-xs text-slate-500">
                          Enter at least one of registration number or
                          certificate number.
                        </p>
                      )}

                      {requiresBackFile(document.documentType) ? (
                        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                          {renderUpload(document, 'front')}
                          {renderUpload(document, 'back')}
                        </div>
                      ) : (
                        <div className="max-w-md">
                          {renderUpload(document, 'front')}
                        </div>
                      )}
                    </div>
                  )}
                </article>
              );
            })
          )}

          {documents.length > 0 && (
            <button
              type="button"
              onClick={handleAddDocument}
              disabled={disabled || availableOptionCount === 0}
              className="flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-[#375AE7] bg-[#375AE7]/20 px-4 py-3 text-sm font-semibold text-[#375AE7] transition hover:border-[#375AE7] hover:bg-[#375AE7]/10 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <PlusIcon />
              Add another document
            </button>
          )}
        </div>
      )}
    </section>
  );
};

export default AdditionalDocumentSection;
